/**
 * Dex live-scan context state machine — Push 9 / latency hardening (2026-06-22).
 *
 * THE PROBLEM (telemetry 2026-06-21, pre-#465). The live scanner would sit on a
 * Pokémon GO Pokédex page for a long time before it registered anything: frame
 * after frame logged `mode:"unknown" variant:null variantSource:"none"
 * confidence:0 appliedThisFrame:0` while the SAME frames carried strong
 * page-level OCR (`POKÉDEX`, `Caught: 8`, region `KANTO`, a `G-MAX` chip). The
 * per-frame adapter (`dexLiveFrameAdapter`) is deliberately pure and has no
 * cross-frame memory, so it cannot say "we have clearly been on the Kanto G-Max
 * page for the last second" — every weak frame restarts from zero.
 *
 * THE FIX. This is a tiny, side-effect-free state machine that separates FAST
 * PAGE ACQUISITION from SAFE COMMIT:
 *
 *   - It LOCKS a provisional scan context (region + variant + colour family)
 *     quickly — within 1-2 good frames — from page-level signals alone, BEFORE
 *     a single dex number has been read. That is purely a hint: a locked context
 *     never writes anything and never relaxes a candidate gate. It exists so a
 *     weak/failed-OCR frame that is still obviously the same dex page can borrow
 *     the last stable region/variant instead of being dropped as `no_variant`.
 *
 *   - The lock is STICKY BUT BOUNDED. A weak frame (OCR read nothing, or the
 *     variant chip read failed) on what is still a dex page reuses the locked
 *     context — but only for a small miss budget of consecutive weak frames
 *     (`MISS_BUDGET`). Once the budget is exhausted the context expires and the
 *     scanner is forced to re-acquire from scratch. This is what stops the
 *     context being "too sticky": it can bridge a brief blur, never an indefinite
 *     wrong-page hold.
 *
 *   - A STRONG CONTRADICTORY SIGNAL forces an immediate revalidation/reset, no
 *     matter how fresh the lock is:
 *       • a different region read confidently (region text changed),
 *       • a different variant chip read confidently (chip ≥ contradiction conf),
 *       • a different page colour family committed by the pre-OCR tracker,
 *       • the frame is no longer a Pokémon GO dex page at all.
 *     The new evidence becomes the candidate for a fresh lock; the OLD context
 *     is discarded so its region/variant can NEVER be applied to the new dex.
 *
 * INVARIANTS (write accuracy is non-negotiable):
 *   - This module decides only the PROVISIONAL region/variant a weak frame may
 *     reuse. Every actual write still passes the unchanged candidate gates in
 *     `dexLiveFrameAdapter` + `dexLiveScanSession` (membership, sprite, slot
 *     range, two-frame corroboration, cross-check, overlay-text). A reused
 *     context only ever fills in a routing hint that those gates then re-judge.
 *   - It is pure and framework-agnostic so the lock/reuse/reset behaviour is
 *     unit-tested deterministically.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * Consecutive good frames a NEW page identity must hold before it locks. One
 * strong frame proposes the context; the second corroborates it. This mirrors
 * the 2-frame bar the variant-stability gate already uses, so a single-frame
 * region/variant flash during a swipe never locks.
 */
export const LOCK_FRAMES_REQUIRED = 2;

/**
 * A page identity that is OVERWHELMINGLY strong on its very first frame may lock
 * instantly (1 frame). "Overwhelming" = region known AND a variant chip read at
 * or above this confidence AND the page colour family agrees. This is the fast-
 * path that gets the scanner off the cold-start "Hold steady" stall within a
 * single clean frame, without lowering the bar for ambiguous reads.
 */
export const INSTANT_LOCK_CHIP_CONFIDENCE = 0.8;

/**
 * Maximum consecutive WEAK frames a locked context survives. A weak frame is one
 * that is still a dex page but produced no fresh page identity (OCR read nothing
 * usable / variant chip failed). After this many in a row the context expires and
 * the scanner must re-acquire — the bound that keeps stickiness honest.
 */
export const MISS_BUDGET = 4;

/**
 * Confidence at or above which a DIFFERENT variant chip read forces a context
 * revalidation. Below this a disagreeing chip is treated as noise and the locked
 * variant is preserved (within the miss budget); at/above it the chip is trusted
 * as a real mid-scan variant switch and the old context is discarded.
 */
export const CONTRADICTION_CHIP_CONFIDENCE = 0.6;

/**
 * MID-SESSION SWITCH (telemetry 2026-06-22). Confidence at or above which a raw
 * chip-text VARIANT-LABEL read (the variant the pill OCR NAMED this frame, before
 * the colour veto / two-frame resolver may have HELD it to null) counts as one
 * vote toward switching the locked context to a new dex.
 *
 * Why a SEPARATE signal from `variant`/`variantConfidence`: on the device the
 * user switched Perfect → G-Max → Purified mid-scan. The resolver correctly read
 * `G-MAX`/`PURIFIED` at ≥0.9 from the pill, but the frozen Nexus app_state pill
 * was still `perfect` and the page-colour tracker mislabelled the page family, so
 * the UNIFIED resolver VETOED the read and returned variant=null (HOLD). The
 * tracker therefore only ever saw `variant:null` and kept a stale `mega` / null
 * lock for the rest of the run — the new dex never acquired. A strong LABEL read
 * is direct evidence of the on-screen tab regardless of what the veto did with it,
 * so we route it here as switch evidence the veto cannot suppress.
 *
 * Lowered 0.9 → 0.8 (telemetry 2026-06-22, focused #476 trace). On device the
 * PURIFIED pill resolved through the active-pill TEXT path at confidence 0.85
 * (clean label + agreeing seafoam hue), NOT the 0.9+ the strong-neighbour path
 * reaches. At 0.9 that real label fell just under the bar, so the only PURIFIED
 * evidence in the whole run was silently dropped and the scanner reaffirmed the
 * stale Perfect lock. 0.8 admits a clean single-label active-pill read (≥0.85)
 * while still excluding the hue-disagree fallback (0.7) and pure noise. A vote is
 * still only a switch REQUEST until SWITCH_VOTES_REQUIRED of them accrue, and a
 * lock change never writes — the unchanged grid/row/two-frame gates still govern.
 */
export const SWITCH_TEXT_CONFIDENCE = 0.8;

/**
 * Consecutive frames a NEW variant must be NAMED by strong chip-text (≠ the
 * locked variant) before the locked context is evicted and re-acquired under the
 * new variant. Strictly > 1 so a single mid-scroll label flash can never tear
 * down a healthy lock, but small so a deliberate tab switch acquires fast (the
 * device cadence is ~1 Hz, so this is ~2 s of sustained new-tab evidence).
 */
export const SWITCH_VOTES_REQUIRED = 2;

/**
 * R14 FIX 1 — CONTEXT-RESET DEBOUNCE (telemetry 2026-06-25, R13 trace).
 *
 * Consecutive frames a contradicting VARIANT or COLOUR signal must PERSIST before
 * the locked context is torn down. The R13 run logged 8 full scan-context resets,
 * each from a SINGLE-frame `appState`/colour flicker while the user never left the
 * locked variant: for one frame `appState` read the wrong variant (`pokemon`) even
 * though the pixel detector still confidently read the variant we were locked on
 * (`purified`). `variantSource:"app_state"` won, the context reset, and the reset
 * zeroed the in-progress streak accumulator + wiped the PiP overlay — cells whose
 * positives straddled the reset never reached the 2-positive gate.
 *
 * A real region/variant/colour transition holds for many frames; a flicker is one.
 * Requiring N CONSECUTIVE contradicting frames of the SAME change before committing
 * the reset rejects the flicker while still tearing down on a genuine sustained
 * switch. Variant-agnostic: this is pure CONTEXT routing, never the apply gate.
 */
export const CONTEXT_CHANGE_DEBOUNCE_FRAMES = 3;

/**
 * R14 FIX 1 — confidence at or above which the per-frame PIXEL detector's variant
 * read counts as "confident" for the appState-flicker guard. When `appState` names
 * a variant that disagrees with the lock BUT the confident pixel detector still
 * reads the LOCKED variant, the appState flip is treated as single-frame noise:
 * the locked variant is preferred for that frame and the disagreement is NOT even
 * counted toward the debounce. Conversely a confident pixel read that AGREES with
 * the NEW variant corroborates a genuine switch and resets immediately (bypassing
 * the debounce). Mirrors the existing chip contradiction bar so a flickered hue
 * read never qualifies.
 */
export const PIXEL_DETECTOR_CONFIDENT_CONFIDENCE = 0.6;

/**
 * Page-level signals extracted from a single frame, BEFORE per-cell dex numbers
 * are trusted. These are exactly the fields the OCR pipeline already produces
 * (`PokedexGridFrame`) plus the pre-OCR colour family the hook tracks; the caller
 * maps a frame onto this shape.
 */
export interface ScanContextSignals {
  /** True when the frame's backdrop/text still reads as a PoGo dex page. */
  isDexPage: boolean;
  /** Region read this frame, or "unknown" when the region text was unreadable. */
  region: string;
  /** Variant the chip/colour resolved this frame, or null when it failed. */
  variant: PokedexVariant | null;
  /** Confidence (0..1) of the variant read this frame. 0 when none. */
  variantConfidence: number;
  /** Pre-OCR page colour family committed for this frame (e.g. "dex_cyan"). */
  colourFamily: string | null;
  /** True when this frame produced at least one usable parsed dex number. */
  hasNumbers: boolean;
  /**
   * MID-SESSION SWITCH (telemetry 2026-06-22). The variant the chip-text (pill
   * OCR) DIRECTLY NAMED this frame, BEFORE the unified resolver's colour veto /
   * hold could suppress it — or null when the pill named nothing this frame. This
   * is independent of `variant` (the post-resolution routed variant): the resolver
   * may HOLD `variant` to null while the pill still clearly reads `G-MAX`. A strong
   * label here that disagrees with the locked variant drives the switch vote so a
   * vetoed-but-real tab change can still re-acquire. Optional: callers that don't
   * supply it (older callers / tests) simply never trigger a text-driven switch.
   */
  strongVariantText?: PokedexVariant | null;
  /** Confidence (0..1) of `strongVariantText`. 0/undefined when none. */
  strongVariantTextConfidence?: number;
  /**
   * R14 FIX 1. The variant the per-frame PIXEL detector read this frame (the raw
   * `result.variant` the adapter resolved from chip/tint/colour), independent of
   * the `appState`-authoritative routed `variant`. When `appState` flips for one
   * frame but the pixel detector still confidently reads the LOCKED variant, the
   * flip is noise and must NOT reset the context. Optional: callers that don't
   * supply it fall back to the plain debounce with no pixel guard.
   */
  pixelVariant?: PokedexVariant | null;
  /** Confidence (0..1) of `pixelVariant`. 0/undefined when none. */
  pixelVariantConfidence?: number;
}

/** The currently locked, provisional scan context. */
export interface LockedScanContext {
  region: string;
  variant: PokedexVariant | null;
  colourFamily: string | null;
}

export type ScanContextEvent =
  /** A new page identity is building toward a lock (not yet locked). */
  | "acquiring"
  /** The context just locked this frame (fast page acquisition). */
  | "locked"
  /** A good frame reaffirmed the existing lock. */
  | "reaffirmed"
  /** A weak frame reused the locked context (within the miss budget). */
  | "reused"
  /** Strong contradictory evidence discarded the lock; re-acquiring. */
  | "reset"
  /**
   * R14 FIX 1. A variant/colour change was SEEN this frame but SUPPRESSED — either
   * a confident pixel detector still read the locked variant (appState flicker
   * noise) or the change has not yet persisted CONTEXT_CHANGE_DEBOUNCE_FRAMES
   * consecutive frames. The lock is HELD (context routes under it); no reset.
   */
  | "change_debounced"
  /** The miss budget was exhausted; the lock expired. */
  | "expired"
  /**
   * A strong chip-text label NAMED a different variant than the lock this frame;
   * the switch vote is building but has not yet reached SWITCH_VOTES_REQUIRED.
   * The old lock is still held (no tear-down on a single flash).
   */
  | "switch_requested"
  /**
   * Repeated strong chip-text evicted the old lock and LOCKED the new variant
   * this frame (mid-session dex switch completed). `lockedThisFrame` is true.
   */
  | "switch_locked"
  /**
   * A switch vote that had been building was abandoned because a frame reaffirmed
   * the ORIGINAL locked variant (the disagreeing labels were transient noise).
   */
  | "switch_rejected"
  /** No dex page and no lock — nothing to do. */
  | "idle";

/** Why the tracker tore down a lock. */
export type ScanContextResetReason =
  | "region_changed"
  | "variant_changed"
  | "colour_changed"
  | "left_dex_page"
  | "miss_budget"
  | "text_switch";

/**
 * Does this reset mean the user moved to a DIFFERENT DEX (as opposed to a
 * different slice of the same one, or simply losing the page)?
 *
 * Device log 2026-08-11: "the PiP HUD didn't clear on switch". The HUD's rolling
 * "Added #…" grid and its scanned/target progress are both keyed to ONE dex, and
 * the only place that cleared them was the `switch_locked` branch — the
 * text-driven mid-session switch. A perfect → purified tab change does not
 * usually take that path: it arrives as an ordinary `reset` with
 * `reason: "variant_changed"` (the trace shows exactly that, then an immediate
 * re-lock onto purified), which cleared the scan evidence but left the previous
 * dex's numbers on the HUD. This predicate is what both paths now agree on.
 *
 *   variant_changed / colour_changed / text_switch → a different dex.
 *     (colour family maps 1:1 onto a dex, so a colour change IS a dex change.)
 *   region_changed → the SAME dex, a different region slice. Kanto's adds are
 *     still this dex's adds, so the HUD must keep them.
 *   left_dex_page / miss_budget → we lost the page, not changed dex. Keeping the
 *     panel means a brief occlusion doesn't wipe what the user just earned.
 *
 * Pure; display-scoping only — it never gates a write.
 */
export function resetMeansDifferentDex(
  reason: ScanContextResetReason | undefined,
): boolean {
  return (
    reason === "variant_changed" ||
    reason === "colour_changed" ||
    reason === "text_switch"
  );
}

export interface ScanContextDecision {
  /** What happened to the context this frame. */
  event: ScanContextEvent;
  /**
   * The context the caller should route this frame under, or null when none is
   * available (acquiring with no prior lock, idle, or just-reset with no fresh
   * identity yet). NEVER a stale context after a contradiction.
   */
  context: LockedScanContext | null;
  /** True only on the frame the lock was first established. */
  lockedThisFrame: boolean;
  /** Consecutive weak frames the current lock has absorbed (0 when fresh/none). */
  misses: number;
  /** Why a reset/expiry happened, for telemetry. Undefined otherwise. */
  reason?: ScanContextResetReason;
  /**
   * R14 FIX 1. On a `change_debounced` event, how many consecutive frames the
   * pending change has now been seen for (0 when the change was suppressed as
   * pixel-detector noise and not counted toward the debounce). Undefined on
   * every other event.
   */
  framesPending?: number;
  /**
   * R14 FIX 1. On a committed `reset` driven by a debounced variant/colour change,
   * the number of consecutive frames that confirmed it (== CONTEXT_CHANGE_DEBOUNCE_
   * FRAMES, or 1 for an immediate region switch / pixel-corroborated variant). Lets
   * a device trace tell a debounced-then-committed reset from an instant one.
   */
  framesConfirmed?: number;
  /**
   * Switch-vote progress, surfaced for telemetry. On `switch_requested` this is
   * the number of consecutive strong-text votes accrued so far (1..N-1); on
   * `switch_locked` it is the vote count that tripped the switch. Undefined when
   * no switch vote is in flight.
   */
  switchVotes?: number;
  /** The variant a switch vote is building toward (telemetry). */
  switchTarget?: PokedexVariant | null;
  /**
   * The variant the lock held WHEN a switch vote was raised — the "from" side of
   * a `switch_requested` / `switch_locked` (telemetry). Lets a device trace read
   * the full transition (e.g. perfect → purified) rather than only the target.
   */
  switchFrom?: PokedexVariant | null;
}

/**
 * True when two page identities are the same dex page. Region + variant +
 * colour family must all agree; a null/"unknown" field on either side is treated
 * as "no contradiction" (the weak frame simply didn't read it), so a partial read
 * does not count as a different page.
 */
function sameIdentity(
  ctx: LockedScanContext,
  region: string,
  variant: PokedexVariant | null,
  colourFamily: string | null,
): boolean {
  if (region !== "unknown" && ctx.region !== "unknown" && region !== ctx.region) {
    return false;
  }
  if (variant != null && ctx.variant != null && variant !== ctx.variant) {
    return false;
  }
  if (
    colourFamily != null &&
    ctx.colourFamily != null &&
    colourFamily !== ctx.colourFamily
  ) {
    return false;
  }
  return true;
}

/**
 * Create a stateful, bounded scan-context tracker. Feed each frame's page-level
 * signals in arrival order; it returns whether a provisional context is locked,
 * which context to route under, and a telemetry event describing what changed.
 */
export function createScanContextTracker() {
  let locked: LockedScanContext | null = null;
  // The page identity currently building toward a lock (candidate), and how many
  // consecutive good frames have agreed with it.
  let candidate: LockedScanContext | null = null;
  let candidateRun = 0;
  // Consecutive weak frames the active lock has absorbed.
  let misses = 0;
  // MID-SESSION SWITCH vote: the variant strong chip-text has been NAMING that
  // disagrees with the current lock, and how many consecutive frames it has done
  // so. Reset whenever the lock's own variant is reaffirmed or the named target
  // changes. Drives the text-driven eviction the colour-vetoed `variant` cannot.
  let switchTarget: PokedexVariant | null = null;
  let switchVotes = 0;
  // R14 FIX 1 — context-change debounce. The contradicting change currently
  // building toward a committed reset (`pendingSignature` = reason+new-value, so a
  // change of WHAT is contradicting restarts the run), and how many CONSECUTIVE
  // frames it has held. Any non-contradicting frame (reaffirm/reuse/idle) clears
  // it, so only a SUSTAINED change tears the lock down.
  let pendingSignature: string | null = null;
  let pendingFrames = 0;
  function clearPending(): void {
    pendingSignature = null;
    pendingFrames = 0;
  }

  function identityFrom(s: ScanContextSignals): LockedScanContext {
    return { region: s.region, variant: s.variant, colourFamily: s.colourFamily };
  }

  /**
   * A frame carries a usable NEW-page identity when it is a dex page AND it read
   * at least a region or a confident variant — enough to say "this is a specific
   * dex", not merely "this is some dex page". A bare dex page with no region and
   * no variant cannot lock anything; it can only reuse an existing lock as a weak
   * frame.
   */
  function hasUsableIdentity(s: ScanContextSignals): boolean {
    if (!s.isDexPage) return false;
    if (s.region !== "unknown") return true;
    if (s.variant != null && s.variantConfidence > 0) return true;
    return false;
  }

  function observe(s: ScanContextSignals): ScanContextDecision {
    // ── Off-page: a frame that is not a dex page is the hardest contradiction.
    // It forces any lock to expire immediately — we are no longer on a dex page,
    // so the old region/variant must never bridge to whatever comes next.
    if (!s.isDexPage) {
      const had = locked != null;
      locked = null;
      candidate = null;
      candidateRun = 0;
      misses = 0;
      switchTarget = null;
      switchVotes = 0;
      clearPending();
      return {
        event: had ? "reset" : "idle",
        context: null,
        lockedThisFrame: false,
        misses: 0,
        reason: had ? "left_dex_page" : undefined,
      };
    }

    // ── MID-SESSION TEXT-DRIVEN SWITCH (telemetry 2026-06-22). A strong chip-text
    // LABEL that NAMES a different variant than the lock is direct evidence of a
    // deliberate tab change, EVEN WHEN the unified resolver vetoed/held this
    // frame's routed `variant` to null (frozen app_state + mislabelled page colour
    // suppressed it on device). Accrue consecutive votes so a single mid-scroll
    // flash can never tear down a healthy lock; once SWITCH_VOTES_REQUIRED votes
    // for the SAME target accrue, evict the stale lock and re-acquire under the new
    // variant. Runs BEFORE the region/variant/colour contradiction below because
    // the routed `variant` is exactly the signal the veto can suppress.
    if (locked) {
      const textVariant = s.strongVariantText ?? null;
      const textConf = s.strongVariantTextConfidence ?? 0;
      const strongTextDisagrees =
        textVariant != null &&
        textConf >= SWITCH_TEXT_CONFIDENCE &&
        locked.variant != null &&
        textVariant !== locked.variant;

      if (strongTextDisagrees) {
        // The variant the lock held when this vote was raised — the "from" side of
        // the transition, captured before any eviction below reassigns `locked`.
        const fromVariant = locked.variant;
        // Vote toward the named target. A change of target restarts the count so
        // only a SUSTAINED read of ONE new variant can switch.
        if (switchTarget === textVariant) {
          switchVotes += 1;
        } else {
          switchTarget = textVariant;
          switchVotes = 1;
        }
        if (switchVotes >= SWITCH_VOTES_REQUIRED) {
          // Eviction + re-acquire: the old lock is discarded so its region/variant
          // can never apply to the new dex, and the new variant locks immediately
          // (the sustained text IS the corroboration the normal 2-frame bar wants).
          const newCtx: LockedScanContext = {
            // Prefer this frame's region when it read one; else carry the old
            // region forward (a tab switch keeps the same region on screen).
            region: s.region !== "unknown" ? s.region : locked.region,
            variant: textVariant,
            // The page colour follows the new variant when this frame committed
            // one; otherwise leave it for the next good frame to refresh.
            colourFamily: s.colourFamily ?? null,
          };
          const votes = switchVotes;
          locked = newCtx;
          candidate = null;
          candidateRun = 0;
          misses = 0;
          switchTarget = null;
          switchVotes = 0;
          clearPending();
          return {
            event: "switch_locked",
            context: { ...newCtx },
            lockedThisFrame: true,
            misses: 0,
            reason: "text_switch",
            switchVotes: votes,
            switchTarget: textVariant,
            switchFrom: fromVariant,
          };
        }
        // Vote building but not yet decisive: hold the old lock, report progress.
        return {
          event: "switch_requested",
          context: { ...locked },
          lockedThisFrame: false,
          misses: 0,
          switchVotes,
          switchTarget,
          switchFrom: fromVariant,
        };
      }

      // A frame that strongly NAMES the locked variant (or routes/agrees with it)
      // abandons any in-flight switch vote: the disagreeing labels were transient.
      if (
        switchVotes > 0 &&
        ((textVariant != null && textVariant === locked.variant) ||
          (s.variant != null && s.variant === locked.variant))
      ) {
        switchTarget = null;
        switchVotes = 0;
        return {
          event: "switch_rejected",
          context: { ...locked },
          lockedThisFrame: false,
          misses: 0,
        };
      }
    }

    // ── Strong contradiction against an existing lock. A confidently-read region
    // or variant that DISAGREES with the lock is a candidate mid-scan switch.
    //
    // R14 FIX 1 — DEBOUNCE. A real region/variant/colour transition holds for many
    // frames; a single-frame appState/colour flicker (telemetry 2026-06-25) does
    // not. So a VARIANT or COLOUR change must PERSIST for CONTEXT_CHANGE_DEBOUNCE_
    // FRAMES consecutive frames before the lock is torn down; a transient 1-2 frame
    // disagreement holds the lock. Two escape hatches keep genuine switches fast:
    //   • a confident PIXEL detector reading the LOCKED variant means the appState
    //     flip is noise — prefer the lock and DON'T even count it toward the
    //     debounce (the exact R13 `variant pixel-detector ignored` flicker), and
    //   • a confident PIXEL detector AGREEING with the NEW variant corroborates a
    //     real switch — reset immediately, bypassing the debounce.
    // A REGION change stays immediate (a hard navigation signal, never the flicker
    // bucket). This is pure context routing; the apply gate is untouched.
    if (locked) {
      const regionContradicts =
        s.region !== "unknown" &&
        locked.region !== "unknown" &&
        s.region !== locked.region;
      const variantContradicts =
        s.variant != null &&
        locked.variant != null &&
        s.variant !== locked.variant &&
        s.variantConfidence >= CONTRADICTION_CHIP_CONFIDENCE;
      const colourContradicts =
        s.colourFamily != null &&
        locked.colourFamily != null &&
        s.colourFamily !== locked.colourFamily;

      // Pixel-detector signals for the appState-flicker guard.
      const pixelVar = s.pixelVariant ?? null;
      const pixelConf = s.pixelVariantConfidence ?? 0;
      const pixelConfident =
        pixelVar != null && pixelConf >= PIXEL_DETECTOR_CONFIDENT_CONFIDENCE;
      const pixelMatchesLocked = pixelConfident && pixelVar === locked.variant;
      const pixelAgreesNewVariant =
        pixelConfident &&
        variantContradicts &&
        s.variant != null &&
        pixelVar === s.variant;

      // Resolve the single change to evaluate this frame, in the established
      // reason priority (region → variant → colour).
      let changeReason: ScanContextDecision["reason"] = undefined;
      let changeSignature = "";
      let immediate = false; // genuine signal → reset now, bypass the debounce
      let suppressNoCount = false; // appState/colour flicker → prefer lock, no count
      if (regionContradicts) {
        changeReason = "region_changed";
        changeSignature = `region:${s.region}`;
        immediate = true;
      } else if (variantContradicts) {
        changeReason = "variant_changed";
        changeSignature = `variant:${s.variant}`;
        if (pixelMatchesLocked) {
          // appState flipped but the pixel detector still reads the locked variant.
          suppressNoCount = true;
        } else if (pixelAgreesNewVariant) {
          immediate = true;
        }
      } else if (colourContradicts) {
        changeReason = "colour_changed";
        changeSignature = `colour:${s.colourFamily}`;
        // A confident pixel read of the LOCKED variant means the page identity is
        // intact — a lone colour-family flip is the same single-frame noise.
        if (pixelMatchesLocked) suppressNoCount = true;
      }

      if (changeReason) {
        if (suppressNoCount) {
          // Prefer the locked context for this frame; do NOT count toward the
          // debounce. The lock is held and keeps routing.
          return {
            event: "change_debounced",
            context: { ...locked },
            lockedThisFrame: false,
            misses,
            reason: changeReason,
            framesPending: 0,
          };
        }
        if (!immediate) {
          // Accrue consecutive-frame evidence for THIS change. A change of what is
          // contradicting (different signature) restarts the run at 1.
          if (pendingSignature === changeSignature) {
            pendingFrames += 1;
          } else {
            pendingSignature = changeSignature;
            pendingFrames = 1;
          }
          if (pendingFrames < CONTEXT_CHANGE_DEBOUNCE_FRAMES) {
            // Not yet sustained: hold the lock, report progress for telemetry.
            return {
              event: "change_debounced",
              context: { ...locked },
              lockedThisFrame: false,
              misses,
              reason: changeReason,
              framesPending: pendingFrames,
            };
          }
        }
        // Commit the reset: a genuine immediate switch, or a change that persisted
        // the full debounce. Discard the old context so it can never apply to the
        // new dex, and seed the contradicting frame as the next lock candidate.
        const framesConfirmed = immediate ? 1 : pendingFrames;
        locked = null;
        misses = 0;
        switchTarget = null;
        switchVotes = 0;
        clearPending();
        if (hasUsableIdentity(s)) {
          candidate = identityFrom(s);
          candidateRun = 1;
        } else {
          candidate = null;
          candidateRun = 0;
        }
        return {
          event: "reset",
          context: null,
          lockedThisFrame: false,
          misses: 0,
          reason: changeReason,
          framesConfirmed,
        };
      }
      // No contradiction this frame — a consecutive run (if any) is broken.
      clearPending();
    }

    // ── A locked context that this frame agrees with (or does not contradict).
    if (locked) {
      if (hasUsableIdentity(s) && sameIdentity(locked, s.region, s.variant, s.colourFamily)) {
        // Good corroborating frame: refresh any read fields, reset the miss
        // budget, and report a reaffirmation.
        misses = 0;
        if (s.region !== "unknown") locked.region = s.region;
        if (s.variant != null) locked.variant = s.variant;
        if (s.colourFamily != null) locked.colourFamily = s.colourFamily;
        return {
          event: "reaffirmed",
          context: { ...locked },
          lockedThisFrame: false,
          misses: 0,
        };
      }
      // Weak frame on what is still a dex page (no usable fresh identity, or a
      // partial read that doesn't contradict): reuse the lock within the bound.
      misses += 1;
      if (misses > MISS_BUDGET) {
        locked = null;
        const expiredMisses = misses;
        misses = 0;
        candidate = null;
        candidateRun = 0;
        switchTarget = null;
        switchVotes = 0;
        clearPending();
        return {
          event: "expired",
          context: null,
          lockedThisFrame: false,
          misses: expiredMisses,
          reason: "miss_budget",
        };
      }
      return {
        event: "reused",
        context: { ...locked },
        lockedThisFrame: false,
        misses,
      };
    }

    // ── No lock yet. Try to build one from a usable page identity.
    if (!hasUsableIdentity(s)) {
      // A bare dex page with nothing to identify it: stay idle, drop any partial
      // candidate (a candidate must be corroborated by CONSECUTIVE good frames).
      candidate = null;
      candidateRun = 0;
      return { event: "idle", context: null, lockedThisFrame: false, misses: 0 };
    }

    // Instant-lock fast path: an overwhelmingly strong first frame (region known
    // + confident chip + colour family present) locks immediately so the scanner
    // leaves the cold-start stall within a single clean frame.
    const overwhelming =
      s.region !== "unknown" &&
      s.variant != null &&
      s.variantConfidence >= INSTANT_LOCK_CHIP_CONFIDENCE &&
      s.colourFamily != null;

    if (candidate && sameIdentity(candidate, s.region, s.variant, s.colourFamily)) {
      candidateRun += 1;
      // Refresh any newly-read fields onto the candidate.
      if (s.region !== "unknown") candidate.region = s.region;
      if (s.variant != null) candidate.variant = s.variant;
      if (s.colourFamily != null) candidate.colourFamily = s.colourFamily;
    } else {
      candidate = identityFrom(s);
      candidateRun = 1;
    }

    if (overwhelming || candidateRun >= LOCK_FRAMES_REQUIRED) {
      locked = { ...candidate };
      candidate = null;
      candidateRun = 0;
      misses = 0;
      return {
        event: "locked",
        context: { ...locked },
        lockedThisFrame: true,
        misses: 0,
      };
    }

    return {
      event: "acquiring",
      context: null,
      lockedThisFrame: false,
      misses: 0,
    };
  }

  return {
    observe,
    /** The currently locked context, or null. */
    current(): LockedScanContext | null {
      return locked ? { ...locked } : null;
    },
    /** Reset between scan sessions. */
    reset(): void {
      locked = null;
      candidate = null;
      candidateRun = 0;
      misses = 0;
      switchTarget = null;
      switchVotes = 0;
      clearPending();
    },
  };
}

export type ScanContextTracker = ReturnType<typeof createScanContextTracker>;
